import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiShield, FiKey, FiLock, FiArrowLeft, FiRefreshCw, FiAlertTriangle } = FiIcons;

const SecuritySettings = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fallbackSections = ['shared', 'luka', 'klara', 'family', 'trips'];

  useEffect(() => {
    fetchSecrets();
  }, []);

  const fetchSecrets = async () => {
    try {
      const { data, error } = await supabase.from('section_secrets_20240520').select('section');
      if (error) throw error;
      
      // If table is empty, use the fallbacks we expect
      if (!data || data.length === 0) {
        setSections(fallbackSections.map(s => ({ section: s })));
      } else {
        setSections(data);
      }
    } catch (err) {
      console.error(err);
      setSections(fallbackSections.map(s => ({ section: s })));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (section) => {
    const newPass = prompt(`Enter new password for the "${section.toUpperCase()}" section:`);
    
    if (newPass === null) return; // Cancelled
    if (!newPass || newPass.length < 4) {
      return toast.error("Password is too short (min 4 characters)");
    }

    setUpdating(section);
    try {
      const { data, error } = await supabase.rpc('update_section_password', {
        sec: section,
        new_pass: newPass
      });

      if (error) throw error;
      toast.success(`Password for ${section} updated successfully!`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update password. Ensure database functions are deployed.");
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="mb-10">
        <button 
          onClick={() => window.history.back()} 
          className="text-sm font-bold text-[var(--accent)] flex items-center mb-2 hover:underline p-2 -ml-2"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-1" /> Back to Dashboard
        </button>
        <h1 className="text-4xl font-serif font-bold mb-3">Security & Access</h1>
        <p className="text-[var(--text-secondary)]">Manage the passwords that protect different branches of the archive.</p>
      </div>

      <div className="space-y-4">
        {sections.map(s => (
          <div key={s.section} className="bg-white p-6 rounded-[1.5rem] border border-[var(--border)] flex items-center justify-between group hover:border-[var(--accent)]/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-stone-50 rounded-2xl flex items-center justify-center group-hover:bg-[var(--accent)] group-hover:text-white transition-all duration-300">
                <SafeIcon icon={FiLock} className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg capitalize">{s.section}</h3>
                <p className="text-xs text-[var(--text-secondary)] font-medium">Password Protected Area</p>
              </div>
            </div>
            <button 
              onClick={() => handleUpdatePassword(s.section)}
              disabled={updating === s.section}
              className="flex items-center gap-2 text-sm font-bold text-[var(--accent)] bg-stone-50 px-5 py-3 rounded-xl hover:bg-[var(--accent)] hover:text-white transition-all active:scale-95 disabled:opacity-50"
            >
              {updating === s.section ? (
                <SafeIcon icon={FiRefreshCw} className="animate-spin" />
              ) : (
                <SafeIcon icon={FiKey} />
              )}
              <span>Change Password</span>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-12 p-8 bg-amber-50 rounded-[2rem] border border-amber-100 flex gap-5">
        <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiShield} className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h4 className="font-bold text-amber-900 text-lg mb-2">Access Control Reminders</h4>
          <ul className="space-y-3">
            <li className="text-sm text-amber-800 flex items-start gap-2">
              <div className="w-1 h-1 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span><strong>Shared Password:</strong> The master key. Unlocks everything for users.</span>
            </li>
            <li className="text-sm text-amber-800 flex items-start gap-2">
              <div className="w-1 h-1 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span><strong>Luka/Klara:</strong> Unlocks their specific page + the Family & Trips collections.</span>
            </li>
            <li className="text-sm text-amber-800 flex items-start gap-2">
              <div className="w-1 h-1 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              <span><strong>Admin:</strong> Your email/password login automatically bypasses all these gates.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;